import { useState, useRef, useCallback, useEffect } from "react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { createClient } from "@supabase/supabase-js";
import * as XLSX from "xlsx";
import * as pdfjsLib from "pdfjs-dist/webpack";

const supabase = createClient(
  "https://udzzcdhlmjzykquseqzj.supabase.co",
  "sb_publishable_L_o7ipPi8DJyR9-JaOreyw_AxoVI5E1"
);

const ADMIN_PASSWORD = "Syndicate2025";

const CARRIERS = [
  "Americo","Foresters","Pacific Life","Transamerica","Mutual of Omaha",
  "F&G","Kansas City Life","American Amicable","American Home Life",
  "United Home Life","InstaBrain","Royal Neighbors","Catholic Financial",
  "Elco Mutual","Illinois Mutual"
];

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const CARRIER_COLORS = [
  "#1e40af","#0369a1","#0891b2","#0d9488","#059669",
  "#65a30d","#ca8a04","#ea580c","#dc2626","#9333ea",
  "#6d28d9","#2563eb","#0284c7","#0e7490","#047857"
];

function LoginScreen({ onLogin }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [shaking, setShaking] = useState(false);
  const handleSubmit = () => {
    if (password === ADMIN_PASSWORD) { onLogin(); }
    else {
      setError(true); setShaking(true);
      setTimeout(() => setShaking(false), 500);
      setTimeout(() => setError(false), 3000);
      setPassword("");
    }
  };
  return (
    <div style={{minHeight:"100vh",background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1d4ed8 100%)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'DM Sans',system-ui,sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700;800&display=swap');
        @keyframes shake{0%,100%{transform:translateX(0)}20%{transform:translateX(-10px)}40%{transform:translateX(10px)}60%{transform:translateX(-8px)}80%{transform:translateX(8px)}}
        @keyframes fadeIn{from{opacity:0;transform:translateY(24px)}to{opacity:1;transform:translateY(0)}}
        .login-card{animation:fadeIn 0.5s ease forwards;}
        .shake{animation:shake 0.5s ease;}
        .login-btn:hover{background:#2563eb !important;transform:translateY(-1px);}
        .login-input:focus{outline:none;border-color:#3b82f6 !important;box-shadow:0 0 0 3px rgba(59,130,246,0.2);}
      `}</style>
      <div className={`login-card ${shaking?"shake":""}`} style={{background:"rgba(255,255,255,0.05)",backdropFilter:"blur(20px)",borderRadius:24,padding:"52px 48px",width:"100%",maxWidth:420,border:"1.5px solid rgba(255,255,255,0.1)",boxShadow:"0 32px 80px rgba(0,0,0,0.4)"}}>
        <div style={{textAlign:"center",marginBottom:36}}>
          <div style={{width:64,height:64,borderRadius:18,background:"linear-gradient(135deg,#3b82f6,#60a5fa)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:28,margin:"0 auto 20px",boxShadow:"0 8px 24px rgba(59,130,246,0.4)"}}>⚡</div>
          <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:26,color:"#fff",fontWeight:800,marginBottom:6}}>Syndicate Financial</h1>
          <p style={{color:"#93c5fd",fontSize:13,letterSpacing:"0.1em",textTransform:"uppercase"}}>Commission Dashboard</p>
        </div>
        <div style={{height:1,background:"rgba(255,255,255,0.1)",marginBottom:32}}/>
        <div style={{marginBottom:16}}>
          <label style={{display:"block",color:"#93c5fd",fontSize:12,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>Admin Password</label>
          <input className="login-input" type="password" value={password} onChange={e=>setPassword(e.target.value)} onKeyDown={e=>e.key==="Enter"&&handleSubmit()} placeholder="Enter password" style={{width:"100%",padding:"14px 16px",borderRadius:12,border:`1.5px solid ${error?"#ef4444":"rgba(255,255,255,0.15)"}`,background:"rgba(255,255,255,0.08)",color:"#fff",fontSize:15,transition:"all 0.2s"}} />
        </div>
        {error && <p style={{color:"#f87171",fontSize:13,marginBottom:16,textAlign:"center",fontWeight:600}}>Incorrect password. Please try again.</p>}
        <button className="login-btn" onClick={handleSubmit} style={{width:"100%",padding:"14px",borderRadius:12,border:"none",background:"linear-gradient(135deg,#1d4ed8,#2563eb)",color:"#fff",fontSize:15,fontWeight:700,cursor:"pointer",transition:"all 0.2s",boxShadow:"0 4px 16px rgba(29,78,216,0.4)"}}>Access Dashboard →</button>
        <p style={{color:"rgba(255,255,255,0.3)",fontSize:11,textAlign:"center",marginTop:24}}>Syndicate Financial · Authorized Personnel Only</p>
      </div>
    </div>
  );
}

function ColumnMapper({ headers, onConfirm, onCancel }) {
  const standardFields = [
    { key: "carrier", label: "Carrier Name" },
    { key: "policy_number", label: "Policy Number" },
    { key: "client_name", label: "Client / Insured Name" },
    { key: "annualized_premium", label: "Annualized Premium" },
    { key: "commission_pct", label: "Commission %" },
    { key: "commission_amount", label: "Commission Amount $" },
    { key: "chargeback_amount", label: "Chargeback Amount" },
    { key: "product_type", label: "Product Type" },
    { key: "month", label: "Month (number)" },
    { key: "year", label: "Year" },
  ];
  const [mapping, setMapping] = useState({});
  return (
    <div style={{background:"#fff",borderRadius:16,padding:32,maxWidth:600,margin:"0 auto",boxShadow:"0 8px 40px rgba(30,64,175,0.13)"}}>
      <h3 style={{fontFamily:"'Playfair Display',serif",color:"#1e3a8a",marginBottom:8}}>Map Your Columns</h3>
      <p style={{color:"#64748b",fontSize:14,marginBottom:24}}>Match your file's column headers to our standard fields.</p>
      <div style={{display:"grid",gap:12}}>
        {standardFields.map(f => (
          <div key={f.key} style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,alignItems:"center"}}>
            <label style={{fontSize:13,fontWeight:600,color:"#374151"}}>{f.label}</label>
            <select value={mapping[f.key]||""} onChange={e=>setMapping({...mapping,[f.key]:e.target.value})} style={{padding:"6px 10px",borderRadius:8,border:"1.5px solid #cbd5e1",fontSize:13,color:"#1e3a8a",background:"#f8fafc"}}>
              <option value="">— skip —</option>
              {headers.map(h=><option key={h} value={h}>{h}</option>)}
            </select>
          </div>
        ))}
      </div>
      <div style={{display:"flex",gap:12,marginTop:28,justifyContent:"flex-end"}}>
        <button onClick={onCancel} style={{padding:"10px 22px",borderRadius:10,border:"1.5px solid #cbd5e1",background:"#fff",color:"#64748b",fontWeight:600,cursor:"pointer",fontSize:14}}>Cancel</button>
        <button onClick={()=>onConfirm(mapping)} style={{padding:"10px 24px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#1e40af,#0369a1)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:14}}>Confirm & Import</button>
      </div>
    </div>
  );
}

// ─── CARRIER TEMPLATES ────────────────────────────────────────────────────────
const CARRIER_TEMPLATES = {
  "American Amicable": {
    name: "American Amicable Advance Report (CSV)",
    description: "Monthly advances CSV — columns: RptDate, Policy, Insured, Anz Prem, Com Rate, Advance, IssDate, Plan",
    autoProcess: true,
    process: (rows, carrier) => {
      const results = [];
      rows.forEach(row => {
        // Skip rows with no policy or adjustment rows
        const policy = (row["Policy"] || "").replace(/[="]/g,"").trim();
        const insured = (row["Insured"] || "").trim();
        if (!policy || insured.includes("ADJUST") || insured.includes("TOTAL")) return;

        const advanceRaw = parseFloat(String(row["Advance"]||"0").replace(/[$,="\s]/g,"")) || 0;
        const annPrem = parseFloat(String(row["Anz Prem"]||"0").replace(/[$,="\s]/g,"")) || 0;
        const commPct = parseFloat(String(row["Com Rate"]||"0").replace(/[="\s]/g,"")) || 0;
        const plan = (row["Plan"]||"").replace(/[="]/g,"").trim();
        const agent = (row["WritingAgent"]||"").replace(/[="]/g,"").trim();

        // Parse date from IssDate (MM/DD/YY format)
        let month = 0, year = 0;
        const dateStr = row["IssDate"] || row["RptDate"] || "";
        const dateParts = dateStr.replace(/[="]/g,"").trim().split("/");
        if (dateParts.length >= 3) {
          month = parseInt(dateParts[0]) || 0;
          const yr = parseInt(dateParts[2]) || 0;
          year = yr < 100 ? 2000 + yr : yr;
        }

        const isChargeback = advanceRaw < 0;
        results.push({
          carrier,
          policy_number: policy,
          client_name: insured,
          annualized_premium: annPrem,
          commission_pct: commPct,
          commission_amount: isChargeback ? 0 : advanceRaw,
          chargeback_amount: isChargeback ? Math.abs(advanceRaw) : 0,
          product_type: plan || "Life",
          month,
          year,
          status: isChargeback ? "Chargeback" : "Paid",
          upload_date: new Date().toISOString().split("T")[0],
          agent_number: agent,
        });
      });
      return results;
    }  },
  "American Home Life": {
    name: "American Home Life Commission Details (Excel)",
    description: "Commission Details tab — Advance Commission rows only",
    autoProcess: true,
    isExcel: true,
    process: (rows, carrier) => {
      const results = [];
      rows.forEach(row => {
        const commType = (row["COMMISSIONTYPE"] || "").trim();
        const commCategory = (row["COMMISSIONCATEGORY"] || "").trim();
        // Only process rows where COMMISSIONCATEGORY is "Advance" (skip "Initial")
        if (commCategory !== "Advance") return;
        
        const policyNum = (row["POLICYNUMBER"] || "").trim();
        const client = (row["CLIENT"] || "").trim();
        if (!policyNum || !client) return;
        
        const commAmount = parseFloat(String(row["COMMISSIONAMOUNT"] || "0").replace(/[$,]/g, "")) || 0;
        const commPremium = parseFloat(String(row["COMMISSIONABLEPREMIUM"] || "0").replace(/[$,]/g, "")) || 0;
        const rate = parseFloat(String(row["RATE%"] || "0").replace(/[%]/g, "")) || 0;
        const product = (row["PRODUCT"] || row["LONGDESCRIPTION"] || "").trim();
        const agent = (row["WRITINGAGENTNUMBER"] || "").trim();
        
        let month = 0, year = 0;
        const dateStr = row["EFFECTIVEDATE"] || row["COMMISSIONPAIDDATE"] || "";
        const dateParts = dateStr.toString().split("/");
        if (dateParts.length >= 3) {
          month = parseInt(dateParts[0]) || 0;
          year = parseInt(dateParts[2]) || 0;
        }
        
        const isChargeback = commAmount < 0;
        results.push({
          carrier,
          policy_number: policyNum,
          client_name: client,
          annualized_premium: commPremium,
          commission_pct: rate,
          commission_amount: isChargeback ? 0 : commAmount,
          chargeback_amount: isChargeback ? Math.abs(commAmount) : 0,
          product_type: product || "Life",
          month, year,
          status: isChargeback ? "Chargeback" : "Paid",
          upload_date: new Date().toISOString().split("T")[0],
          agent_number: agent,
        });
      });
      return results;
    }
  },
  "Kansas City Life": {
    name: "Kansas City Life Agent Statement (PDF)",
    description: "Weekly commission statements — auto-extracts from PDF table",
    autoProcess: true,
    isPDF: true,
    process: (text, carrier) => {
      console.log("=== KCL PDF PROCESSING START ===");
      console.log("Text length:", text.length);
      console.log("First 1000 characters:", text.substring(0, 1000));
      console.log("Searching for PAY PERIOD...");
      
      const results = [];
      
      // Extract pay period date from header (applies to all rows)
      // Try multiple patterns to handle different PDF extraction formats
      let payPeriodMatch = text.match(/PAY\s*PERIOD[:\s]*Ending\s*(\d{1,2})\s*\/\s*(\d{1,2})\s*\/\s*(\d{4})/i);
      
      if (!payPeriodMatch) {
        // Try alternate format without slashes
        payPeriodMatch = text.match(/PAY\s*PERIOD[:\s]*Ending\s*(\d{1,2})\s*(\d{1,2})\s*(\d{4})/i);
      }
      
      if (!payPeriodMatch) {
        console.error("KCL: Could not find PAY PERIOD date in PDF");
        console.log("Text sample around 'PAY':", text.substring(text.indexOf('PAY'), text.indexOf('PAY') + 200));
        return results;
      }
      
      const month = parseInt(payPeriodMatch[1]);
      const year = parseInt(payPeriodMatch[3] || payPeriodMatch[2]);
      
      console.log(`✅ KCL: Extracted pay period - Month: ${month}, Year: ${year}`);
      
      // Parse commission rows - handle missing PLAN field better
      // Pattern: DATE JNT POLICY# NAME [PLAN?] ANNIV YR PREM RATE EARNINGS EXPLANATION
      const commissionRegex = /\d{1,2}\/\d{2}\s+(\d+)\s+([A-Z0-9]{7,10})\s+([\w]+)\s+([A-Z0-9]+)?\s*(\d{1,2}\/\d{2})\s+(\d+)\s+\$?([\d,.-]+)\s+([\d.]+)\s+(-?\$?[\d,.-]+)\s+(COMMISSION ADVANCE|AS EARNED COMMISSION|ADVANCE ON ISSUE|ADVANCE RECOUPMENT)/gi;
      
      let match;
      while ((match = commissionRegex.exec(text)) !== null) {
        const [_, jnt, policyNum, clientName, planOrAnniv, possibleAnniv, yr, premStr, rateStr, earningsStr, explanation] = match;
        
        // Determine if we have a PLAN or not based on whether planOrAnniv looks like a date
        const hasPlan = planOrAnniv && !planOrAnniv.match(/^\d{1,2}\/\d{2}$/);
        const plan = hasPlan ? planOrAnniv : "";
        
        const premium = parseFloat(premStr.replace(/[$,]/g, '')) || 0;
        const rate = parseFloat(rateStr) || 0;
        const earnings = parseFloat(earningsStr.replace(/[$,]/g, '')) || 0;
        
        const isChargeback = explanation.includes('RECOUPMENT') || earnings < 0;
        
        results.push({
          carrier,
          policy_number: policyNum,
          client_name: clientName.trim(),
          annualized_premium: premium,
          commission_pct: rate,
          commission_amount: isChargeback ? 0 : Math.abs(earnings),
          chargeback_amount: isChargeback ? Math.abs(earnings) : 0,
          product_type: plan.trim(),
          month,
          year,
          status: isChargeback ? "Chargeback" : "Paid",
          upload_date: new Date().toISOString().split("T")[0],
          agent_number: "",
        });
      }
      
      return results;
    }
  },
  "Mutual of Omaha": {
    name: "Mutual of Omaha Detailed Statement (Excel)",
    description: "Excel export from portal — DETAILS sheet with REISS/REBILL and NEW ISS PYMT rows",
    autoProcess: true,
    isExcel: true,
    process: (rows, carrier) => {
      console.log("=== MOO EXCEL PROCESSING START ===");
      console.log("MOO: Total rows received:", rows.length);
      console.log("MOO: First row sample:", rows[0]);
      console.log("MOO: First row keys:", rows[0] ? Object.keys(rows[0]) : "no rows");
      
      const results = [];
      
      rows.forEach((row, idx) => {
        const activityType = (row["Activity Type"] || "").trim();
        
        if (idx < 3) {
          console.log(`MOO Row ${idx}: Activity Type='${activityType}'`);
        }
        
        // Only process REISS/REBILL and NEW ISS PYMT
        if (activityType !== "REISS/REBILL" && activityType !== "NEW ISS PYMT") return;
        
        const policyNum = (row["Policy"] || "").trim();
        const clientName = (row["Insureds Name"] || "").trim();
        
        if (!policyNum || !clientName) return;
        
        const premium = parseFloat(row["Comm Premium"] || 0) || 0;
        const rate = parseFloat(row["Comm Pct"] || 0) * 100 || 0; // Convert 0.2 to 20%
        const commission = parseFloat(row["Adv Comm"] || 0) || 0;
        const plan = (row["Plan Code"] || "").trim();
        
        // Parse activity date for month/year
        let month = 0, year = 0;
        const activityDate = row["Activity Date"];
        if (activityDate) {
          const dateStr = activityDate.toString();
          const dateParts = dateStr.split("/");
          if (dateParts.length >= 3) {
            month = parseInt(dateParts[0]) || 0;
            year = parseInt(dateParts[2]) || 0;
          }
        }
        
        const isChargeback = commission < 0;
        
        results.push({
          carrier,
          policy_number: policyNum,
          client_name: clientName,
          annualized_premium: premium,
          commission_pct: rate,
          commission_amount: isChargeback ? 0 : Math.abs(commission),
          chargeback_amount: isChargeback ? Math.abs(commission) : 0,
          product_type: plan,
          month,
          year,
          status: isChargeback ? "Chargeback" : "Paid",
          upload_date: new Date().toISOString().split("T")[0],
          agent_number: "",
        });
      });
      
      return results;
    }
  }

};

// ─── UPLOAD PANEL ─────────────────────────────────────────────────────────────
function UploadPanel({ onImport }) {
  const [dragging, setDragging] = useState(false);
  const [stage, setStage] = useState("idle"); // idle | preview | duplicates | saving
  const [fileNames, setFileNames] = useState([]);
  const [mappedData, setMappedData] = useState([]);
  const [saving, setSaving] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [duplicates, setDuplicates] = useState([]);
  const [newRecords, setNewRecords] = useState([]);
  const [processing, setProcessing] = useState(false);
  const fileRef = useRef();

  const availableTemplates = selectedCarrier && CARRIER_TEMPLATES[selectedCarrier]
    ? [CARRIER_TEMPLATES[selectedCarrier]]
    : [];

  // Robust CSV parser — handles ="..." Excel wrappers and quoted fields
  const parseCSV = (text) => {
    const parseRow = (line) => {
      const vals = [];
      let cur = "", inQuote = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuote = !inQuote; }
        else if (ch === "," && !inQuote) { vals.push(cur.trim().replace(/^=?"?|"?$/g, "")); cur = ""; }
        else { cur += ch; }
      }
      vals.push(cur.trim().replace(/^=?"?|"?$/g, ""));
      return vals;
    };
    const lines = text.trim().split("\n");
    const headers = parseRow(lines[0]);
    const rows = lines.slice(1).map(line => {
      const vals = parseRow(line);
      const obj = {};
      headers.forEach((h, i) => obj[h] = vals[i] || "");
      return obj;
    });
    return { headers, rows };
  };

  const processFile = async (data, fname, isArrayBuffer = false) => {
    const ext = fname.split(".").pop().toLowerCase();
    let rows = [];
    
    if (ext === "pdf" && isArrayBuffer) {
      // Parse PDF using pdfjs
      const loadingTask = pdfjsLib.getDocument({ data });
      const pdf = await loadingTask.promise;
      let fullText = "";
      
      for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
        const page = await pdf.getPage(pageNum);
        const textContent = await page.getTextContent();
        const pageText = textContent.items.map(item => item.str).join(" ");
        fullText += pageText + "\n";
      }
      
      // Return as special format for PDF templates
      return { isPDF: true, text: fullText };
    } else if (["xlsx", "xls"].includes(ext) && isArrayBuffer) {
      // Parse Excel file using SheetJS
      const workbook = XLSX.read(data, { type: "array" });
      // Use "Commission Details" sheet if it exists, otherwise first sheet
      const sheetName = workbook.SheetNames.includes("Commission Details") 
        ? "Commission Details" 
        : workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      // For American Home Life, row 1 is title, row 2 is headers - skip first row
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { range: 1 });
      rows = jsonData;
    } else if (["htm","html"].includes(ext)) {
      const parser = new DOMParser();
      const doc = parser.parseFromString(data, "text/html");
      const table = doc.querySelector("table");
      if (table) {
        const trows = Array.from(table.querySelectorAll("tr"));
        const headers = Array.from(trows[0].querySelectorAll("th,td")).map(c => c.textContent.trim());
        rows = trows.slice(1).map(r => {
          const cells = Array.from(r.querySelectorAll("td")).map(c => c.textContent.trim());
          const obj = {};
          headers.forEach((h, i) => obj[h] = cells[i] || "");
          return obj;
        });
      }
    } else {
      // CSV
      const { rows: csvRows } = parseCSV(data);
      rows = csvRows;
    }
    return rows;
  };

  const handleFiles = async (files) => {
    if (!selectedCarrier) { alert("Please select a carrier first!"); return; }
    if (!selectedTemplate) { alert("Please select a template first!"); return; }

    const fileArray = Array.from(files);
    setProcessing(true);

    // Validate all files are same type (by extension)
    const extensions = fileArray.map(f => f.name.split(".").pop().toLowerCase());
    const uniqueExts = [...new Set(extensions)];
    if (uniqueExts.length > 1) {
      alert(`All files must be the same type. You have: ${uniqueExts.join(", ")}`);
      setProcessing(false);
      return;
    }

    setFileNames(fileArray.map(f => f.name));

    // Read all files
    const allRows = [];
    for (const file of fileArray) {
      const ext = file.name.split(".").pop().toLowerCase();
      const isExcel = ["xlsx", "xls"].includes(ext);
      
      await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const isPDF = ext === "pdf";
          const result = await processFile(
            (isExcel || isPDF) ? new Uint8Array(e.target.result) : e.target.result, 
            file.name, 
            (isExcel || isPDF)
          );
          
          if (result && result.isPDF) {
            // Store PDF text for later processing by template
            allRows.push({ isPDF: true, text: result.text });
          } else {
            allRows.push(...(result || []));
          }
          resolve();
        };
        if (isExcel || ext === "pdf") {
          reader.readAsArrayBuffer(file);
        } else {
          reader.readAsText(file);
        }
      });
    }

    // Process through template
    const template = CARRIER_TEMPLATES[selectedCarrier];
    let processed;
    
    if (template.isPDF && allRows.length > 0 && allRows[0].isPDF) {
      // PDF template - process each PDF separately to preserve individual dates
      processed = [];
      for (const pdfData of allRows) {
        const results = template.process(pdfData.text, selectedCarrier);
        processed.push(...results);
      }
    } else {
      // Regular template - pass rows
      processed = template.process(allRows, selectedCarrier);
    }

    setMappedData(processed);
    setProcessing(false);
    setStage("preview");
  };

  const checkDuplicates = async () => {
    setSaving(true);
    try {
      // Fetch existing policy+carrier combos from DB
      const { data: existing, error } = await supabase
        .from("commissions")
        .select("policy_number, carrier");
      if (error) throw error;

      const existingSet = new Set(
        (existing || []).map(r => `${r.carrier}||${r.policy_number}`)
      );

      // Chargebacks are not duplicates — they offset previous sales
      const dupes = mappedData.filter(r =>
        r.policy_number && existingSet.has(`${r.carrier}||${r.policy_number}`) && r.chargeback_amount === 0
      );
      const fresh = mappedData.filter(r =>
        !r.policy_number || !existingSet.has(`${r.carrier}||${r.policy_number}`) || r.chargeback_amount > 0
      );

      setDuplicates(dupes);
      setNewRecords(fresh);

      if (dupes.length === 0) {
        // No duplicates — save directly
        await saveRecords(mappedData);
      } else {
        setStage("duplicates");
      }
    } catch (err) {
      alert("Error checking duplicates: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const saveRecords = async (records) => {
    setSaving(true);
    try {
      const { error } = await supabase.from("commissions").insert(records);
      if (error) throw error;
      onImport(records.length);
      setStage("idle");
      setMappedData([]); setFileNames([]);
      setDuplicates([]); setNewRecords([]);
      setSelectedCarrier(""); setSelectedTemplate("");
    } catch (err) {
      alert("Error saving: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    if (e.dataTransfer.files.length > 0) handleFiles(e.dataTransfer.files);
  }, [selectedCarrier, selectedTemplate]);

  const selectStyle = {padding:"8px 14px",borderRadius:10,border:"1.5px solid #e2e8f0",fontSize:14,color:"#374151",background:"#fff",fontWeight:500,cursor:"pointer",width:"100%"};
  const fmtFull = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  // ── DUPLICATES SCREEN ──────────────────────────────────────────────────────
  if (stage === "duplicates") return (
    <div style={{padding:32}}>
      <div style={{marginBottom:20,padding:"16px 20px",background:"#fffbeb",borderRadius:12,border:"2px solid #fbbf24",display:"flex",gap:12,alignItems:"flex-start"}}>
        <span style={{fontSize:24}}>⚠️</span>
        <div>
          <p style={{color:"#92400e",fontWeight:800,fontSize:15,marginBottom:4}}>Duplicates Detected</p>
          <p style={{color:"#a16207",fontSize:13}}>
            <strong>{duplicates.length} records</strong> already exist in the database (matched by policy number + carrier).
            <strong> {newRecords.length} records</strong> are new and ready to import.
          </p>
        </div>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:16,marginBottom:24}}>
        <div style={{background:"#fef2f2",borderRadius:12,border:"1.5px solid #fca5a5",padding:16}}>
          <p style={{color:"#dc2626",fontWeight:700,fontSize:13,marginBottom:12}}>⚠️ {duplicates.length} Duplicate Records</p>
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {duplicates.slice(0,20).map((r,i)=>(
              <div key={i} style={{padding:"6px 10px",background:"#fff",borderRadius:8,marginBottom:6,fontSize:12,display:"flex",justifyContent:"space-between"}}>
                <span style={{color:"#374151",fontWeight:600}}>{r.client_name||"—"}</span>
                <span style={{color:"#64748b",fontFamily:"monospace"}}>{r.policy_number}</span>
              </div>
            ))}
            {duplicates.length > 20 && <p style={{color:"#94a3b8",fontSize:12,textAlign:"center"}}>+ {duplicates.length-20} more</p>}
          </div>
        </div>
        <div style={{background:"#f0fdf4",borderRadius:12,border:"1.5px solid #bbf7d0",padding:16}}>
          <p style={{color:"#16a34a",fontWeight:700,fontSize:13,marginBottom:12}}>✅ {newRecords.length} New Records</p>
          <div style={{maxHeight:200,overflowY:"auto"}}>
            {newRecords.slice(0,20).map((r,i)=>(
              <div key={i} style={{padding:"6px 10px",background:"#fff",borderRadius:8,marginBottom:6,fontSize:12,display:"flex",justifyContent:"space-between"}}>
                <span style={{color:"#374151",fontWeight:600}}>{r.client_name||"—"}</span>
                <span style={{color:"#64748b",fontFamily:"monospace"}}>{r.policy_number}</span>
              </div>
            ))}
            {newRecords.length > 20 && <p style={{color:"#94a3b8",fontSize:12,textAlign:"center"}}>+ {newRecords.length-20} more</p>}
          </div>
        </div>
      </div>

      <div style={{display:"flex",gap:12,justifyContent:"flex-end",flexWrap:"wrap"}}>
        <button onClick={()=>setStage("idle")} style={{padding:"10px 22px",borderRadius:10,border:"1.5px solid #cbd5e1",background:"#fff",color:"#64748b",fontWeight:600,cursor:"pointer",fontSize:14}}>✕ Cancel — Import Nothing</button>
        {newRecords.length > 0 && (
          <button onClick={()=>saveRecords(newRecords)} disabled={saving} style={{padding:"10px 22px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#059669,#047857)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:14,opacity:saving?0.7:1}}>
            {saving?"Saving...": `✓ Import New Only (${newRecords.length} records)`}
          </button>
        )}
        <button onClick={()=>saveRecords(mappedData)} disabled={saving} style={{padding:"10px 22px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#d97706,#b45309)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:14,opacity:saving?0.7:1}}>
          {saving?"Saving...": `Import All Including Duplicates (${mappedData.length} records)`}
        </button>
      </div>
    </div>
  );

  // ── PREVIEW SCREEN ─────────────────────────────────────────────────────────
  if (stage === "preview") return (
    <div style={{padding:32}}>
      <div style={{marginBottom:20,padding:"14px 20px",background:"#f0fdf4",borderRadius:10,border:"1.5px solid #bbf7d0",display:"flex",alignItems:"center",gap:12,flexWrap:"wrap"}}>
        <span style={{fontSize:20}}>✅</span>
        <div style={{flex:1}}>
          <p style={{color:"#15803d",fontWeight:700,fontSize:14}}>{selectedCarrier} — {mappedData.length} records parsed from {fileNames.length} file{fileNames.length>1?"s":""}</p>
          <p style={{color:"#16a34a",fontSize:12,marginTop:2}}>
            {fileNames.join(" · ")}
          </p>
        </div>
        <div style={{display:"flex",gap:16,flexWrap:"wrap"}}>
          <span style={{color:"#059669",fontWeight:700,fontSize:13}}>💰 Commissions: {fmtFull(mappedData.filter(r=>r.commission_amount>0).reduce((s,r)=>s+r.commission_amount,0))}</span>
          <span style={{color:"#dc2626",fontWeight:700,fontSize:13}}>⚠️ Chargebacks: {mappedData.filter(r=>r.chargeback_amount>0).length} records</span>
        </div>
      </div>
      <div style={{overflowX:"auto",borderRadius:12,border:"1.5px solid #e2e8f0",marginBottom:24}}>
        <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
          <thead>
            <tr style={{background:"#f1f5f9"}}>
              {["Client","Policy #","Product","Month/Year","Ann. Premium","Commission $","Chargeback","Status"].map(h=>(
                <th key={h} style={{padding:"10px 14px",textAlign:"left",color:"#475569",fontWeight:700,whiteSpace:"nowrap"}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {mappedData.slice(0,15).map((r,i)=>(
              <tr key={i} style={{borderTop:"1px solid #f1f5f9",background:i%2===0?"#fff":"#fafbfc"}}>
                <td style={{padding:"9px 14px",color:"#374151"}}>{r.client_name||"—"}</td>
                <td style={{padding:"9px 14px",color:"#64748b",fontFamily:"monospace",fontSize:11}}>{r.policy_number||"—"}</td>
                <td style={{padding:"9px 14px",color:"#64748b"}}>{r.product_type||"—"}</td>
                <td style={{padding:"9px 14px",color:"#64748b"}}>{r.month?["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"][r.month-1]:"—"} {r.year||""}</td>
                <td style={{padding:"9px 14px",color:"#059669",fontWeight:600}}>{fmtFull(r.annualized_premium)}</td>
                <td style={{padding:"9px 14px",color:"#1d4ed8",fontWeight:600}}>{fmtFull(r.commission_amount)}</td>
                <td style={{padding:"9px 14px",color:r.chargeback_amount>0?"#dc2626":"#64748b"}}>{r.chargeback_amount>0?`-${fmtFull(r.chargeback_amount)}`:"—"}</td>
                <td style={{padding:"9px 14px"}}>
                  <span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:r.status==="Chargeback"?"#fef2f2":"#f0fdf4",color:r.status==="Chargeback"?"#dc2626":"#16a34a"}}>{r.status}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {mappedData.length > 15 && <p style={{color:"#94a3b8",fontSize:13,marginBottom:16}}>+ {mappedData.length-15} more records not shown</p>}
      <div style={{display:"flex",gap:12,justifyContent:"flex-end"}}>
        <button onClick={()=>setStage("idle")} style={{padding:"10px 22px",borderRadius:10,border:"1.5px solid #cbd5e1",background:"#fff",color:"#64748b",fontWeight:600,cursor:"pointer",fontSize:14}}>← Start Over</button>
        <button onClick={checkDuplicates} disabled={saving} style={{padding:"10px 28px",borderRadius:10,border:"none",background:"linear-gradient(135deg,#1e40af,#0369a1)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:14,opacity:saving?0.7:1}}>
          {saving?"Checking for Duplicates...": `→ Check & Save (${mappedData.length} records)`}
        </button>
      </div>
    </div>
  );

  // ── IDLE / UPLOAD SCREEN ───────────────────────────────────────────────────
  return (
    <div style={{padding:32}}>
      <h3 style={{fontFamily:"'Playfair Display',serif",color:"#1e3a8a",marginBottom:6}}>Upload Commission Statements</h3>
      <p style={{color:"#64748b",fontSize:14,marginBottom:28}}>Select carrier and template first, then upload one or multiple files of the same type.</p>

      <div style={{marginBottom:20}}>
        <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
          <div style={{width:28,height:28,borderRadius:"50%",background:selectedCarrier?"#1d4ed8":"#cbd5e1",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0}}>1</div>
          <label style={{fontWeight:700,color:"#0f172a",fontSize:14}}>Select Carrier</label>
        </div>
        <select value={selectedCarrier} onChange={e=>{setSelectedCarrier(e.target.value);setSelectedTemplate("");}} style={selectStyle}>
          <option value="">— Choose a carrier —</option>
          {CARRIERS.map(c=><option key={c} value={c}>{c}{CARRIER_TEMPLATES[c]?" ✓ Template Available":""}</option>)}
        </select>
      </div>

      {selectedCarrier && (
        <div style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:selectedTemplate?"#1d4ed8":"#cbd5e1",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0}}>2</div>
            <label style={{fontWeight:700,color:"#0f172a",fontSize:14}}>Select Template</label>
          </div>
          {availableTemplates.length > 0 ? (
            <div style={{display:"grid",gap:10}}>
              {availableTemplates.map((t,i)=>(
                <div key={i} onClick={()=>setSelectedTemplate(t.name)} style={{padding:"14px 16px",borderRadius:12,border:`2px solid ${selectedTemplate===t.name?"#1d4ed8":"#e2e8f0"}`,background:selectedTemplate===t.name?"#eff6ff":"#fff",cursor:"pointer",transition:"all 0.15s"}}>
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${selectedTemplate===t.name?"#1d4ed8":"#cbd5e1"}`,background:selectedTemplate===t.name?"#1d4ed8":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                      {selectedTemplate===t.name&&<div style={{width:8,height:8,borderRadius:"50%",background:"#fff"}}/>}
                    </div>
                    <div>
                      <p style={{fontWeight:700,color:"#0f172a",fontSize:13}}>{t.name}</p>
                      <p style={{color:"#64748b",fontSize:12,marginTop:2}}>{t.description}</p>
                    </div>
                    <span style={{marginLeft:"auto",padding:"3px 10px",borderRadius:20,background:"#f0fdf4",color:"#16a34a",fontWeight:700,fontSize:11,whiteSpace:"nowrap"}}>Auto-Parse</span>
                  </div>
                </div>
              ))}
              <div onClick={()=>setSelectedTemplate("manual")} style={{padding:"14px 16px",borderRadius:12,border:`2px solid ${selectedTemplate==="manual"?"#1d4ed8":"#e2e8f0"}`,background:selectedTemplate==="manual"?"#eff6ff":"#fff",cursor:"pointer",transition:"all 0.15s"}}>
                <div style={{display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:18,height:18,borderRadius:"50%",border:`2px solid ${selectedTemplate==="manual"?"#1d4ed8":"#cbd5e1"}`,background:selectedTemplate==="manual"?"#1d4ed8":"#fff",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                    {selectedTemplate==="manual"&&<div style={{width:8,height:8,borderRadius:"50%",background:"#fff"}}/>}
                  </div>
                  <div>
                    <p style={{fontWeight:700,color:"#0f172a",fontSize:13}}>Manual Column Mapping</p>
                    <p style={{color:"#64748b",fontSize:12,marginTop:2}}>Map columns yourself for a different report format</p>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div style={{padding:"14px 16px",borderRadius:12,border:"2px solid #e2e8f0",background:"#fafafa"}}>
              <p style={{color:"#64748b",fontSize:13,marginBottom:8}}>No template yet for {selectedCarrier} — you'll map columns manually.</p>
              <button onClick={()=>setSelectedTemplate("manual")} style={{padding:"6px 16px",borderRadius:8,border:"none",background:"#1d4ed8",color:"#fff",fontWeight:600,cursor:"pointer",fontSize:13}}>Use Manual Mapping</button>
            </div>
          )}
        </div>
      )}

      {selectedCarrier && selectedTemplate && (
        <div style={{marginBottom:20}}>
          <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:10}}>
            <div style={{width:28,height:28,borderRadius:"50%",background:"#1d4ed8",color:"#fff",display:"flex",alignItems:"center",justifyContent:"center",fontWeight:800,fontSize:13,flexShrink:0}}>3</div>
            <label style={{fontWeight:700,color:"#0f172a",fontSize:14}}>Upload Files</label>
            <span style={{padding:"3px 10px",borderRadius:20,background:"#eff6ff",color:"#1d4ed8",fontWeight:600,fontSize:11}}>Multiple files OK — same type only</span>
          </div>
          <div
            onDragOver={e=>{e.preventDefault();setDragging(true);}}
            onDragLeave={()=>setDragging(false)}
            onDrop={handleDrop}
            onClick={()=>fileRef.current.click()}
            style={{border:`2.5px dashed ${dragging?"#1d4ed8":"#bfdbfe"}`,borderRadius:16,padding:"40px 32px",textAlign:"center",cursor:"pointer",background:dragging?"#eff6ff":"#f8fafc",transition:"all 0.2s",opacity:processing?0.6:1}}
          >
            {processing ? (
              <>
                <div style={{fontSize:36,marginBottom:12}}>⏳</div>
                <p style={{color:"#1e40af",fontWeight:700,fontSize:15}}>Processing files...</p>
              </>
            ) : (
              <>
                <div style={{fontSize:40,marginBottom:12}}>📂</div>
                <p style={{color:"#1e40af",fontWeight:700,fontSize:15,marginBottom:4}}>Drop files here or click to browse</p>
                <p style={{color:"#94a3b8",fontSize:13,marginBottom:8}}>CSV · HTM · Excel (.xlsx) · PDF — select multiple with Cmd/Ctrl+Click</p>
                <div style={{padding:"8px 16px",background:"#eff6ff",borderRadius:8,display:"inline-block"}}>
                  <span style={{color:"#1d4ed8",fontSize:12,fontWeight:600}}>{selectedCarrier} · {selectedTemplate==="manual"?"Manual Mapping":"Auto-Parse"}</span>
                </div>
              </>
            )}
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls,.htm,.html,.pdf" multiple style={{display:"none"}} onChange={e=>e.target.files.length>0&&handleFiles(e.target.files)} />
          </div>
        </div>
      )}

      {!selectedCarrier && (
        <div style={{padding:"20px",background:"#fafafa",borderRadius:12,border:"1.5px solid #e2e8f0",textAlign:"center"}}>
          <p style={{color:"#94a3b8",fontSize:14}}>👆 Select a carrier above to get started</p>
        </div>
      )}

      <div style={{marginTop:24,padding:"16px 20px",background:"#f8fafc",borderRadius:12,border:"1.5px solid #e2e8f0"}}>
        <p style={{color:"#64748b",fontSize:12,fontWeight:700,marginBottom:8,textTransform:"uppercase",letterSpacing:"0.06em"}}>Templates Available</p>
        <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
          {Object.keys(CARRIER_TEMPLATES).map(c=>(
            <span key={c} style={{padding:"4px 12px",borderRadius:20,background:"#f0fdf4",color:"#16a34a",fontWeight:600,fontSize:12}}>✓ {c}</span>
          ))}
          {CARRIERS.filter(c=>!CARRIER_TEMPLATES[c]).map(c=>(
            <span key={c} style={{padding:"4px 12px",borderRadius:20,background:"#f1f5f9",color:"#94a3b8",fontWeight:600,fontSize:12}}>{c}</span>
          ))}
        </div>
      </div>
    </div>
  );
}


function KPICard({ label, value, sub, color, icon }) {
  return (
    <div style={{background:"#fff",borderRadius:16,padding:"24px 28px",boxShadow:"0 2px 16px rgba(30,64,175,0.07)",border:"1.5px solid #e2e8f0",position:"relative",overflow:"hidden"}}>
      <div style={{position:"absolute",top:0,left:0,right:0,height:4,background:color,borderRadius:"16px 16px 0 0"}}/>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start"}}>
        <div>
          <p style={{color:"#64748b",fontSize:12,fontWeight:600,letterSpacing:"0.08em",textTransform:"uppercase",marginBottom:8}}>{label}</p>
          <p style={{fontSize:28,fontWeight:800,color:"#0f172a",fontFamily:"'Playfair Display',serif",lineHeight:1}}>{value}</p>
          {sub && <p style={{color:"#94a3b8",fontSize:12,marginTop:6}}>{sub}</p>}
        </div>
        <span style={{fontSize:32,opacity:0.15}}>{icon}</span>
      </div>
    </div>
  );
}

export default function App() {
  const [loggedIn, setLoggedIn] = useState(false);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [filterCarrier, setFilterCarrier] = useState("All");
  const [filterYear, setFilterYear] = useState("All");
  const [filterQuarter, setFilterQuarter] = useState("All");
  const [filterMonth, setFilterMonth] = useState("All");
  const [filterProduct, setFilterProduct] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  useEffect(() => { if (loggedIn) fetchRecords(); }, [loggedIn]);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from("commissions").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      setRecords(data || []);
    } catch (err) {
      alert("Error loading data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  // ── Delete functions ─────────────────────────────────────────────────────
  const deleteRecord = async (id) => {
    if (!window.confirm("Delete this record?")) return;
    try {
      const { error } = await supabase.from("commissions").delete().eq("id", id);
      if (error) throw error;
      setRecords(prev => prev.filter(r => r.id !== id));
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });
    } catch (err) { alert("Error deleting: " + err.message); }
  };

  const deleteSelected = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedIds.size} selected records?`)) return;
    setDeleting(true);
    try {
      const ids = Array.from(selectedIds);
      const { error } = await supabase.from("commissions").delete().in("id", ids);
      if (error) throw error;
      setRecords(prev => prev.filter(r => !selectedIds.has(r.id)));
      setSelectedIds(new Set());
    } catch (err) { alert("Error deleting: " + err.message); }
    finally { setDeleting(false); }
  };

  const deleteByCarrier = async (carrier) => {
    const count = records.filter(r => r.carrier === carrier).length;
    if (!window.confirm(`Delete ALL ${count} records for ${carrier}?`)) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("commissions").delete().eq("carrier", carrier);
      if (error) throw error;
      setRecords(prev => prev.filter(r => r.carrier !== carrier));
      setSelectedIds(new Set());
    } catch (err) { alert("Error deleting: " + err.message); }
    finally { setDeleting(false); }
  };

  const deleteByUploadDate = async (date) => {
    const count = records.filter(r => r.upload_date === date).length;
    if (!window.confirm(`Delete ALL ${count} records uploaded on ${date}?`)) return;
    setDeleting(true);
    try {
      const { error } = await supabase.from("commissions").delete().eq("upload_date", date);
      if (error) throw error;
      setRecords(prev => prev.filter(r => r.upload_date !== date));
      setSelectedIds(new Set());
    } catch (err) { alert("Error deleting: " + err.message); }
    finally { setDeleting(false); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filtered.length) { setSelectedIds(new Set()); }
    else { setSelectedIds(new Set(filtered.map(r => r.id))); }
  };

  if (!loggedIn) return <LoginScreen onLogin={() => setLoggedIn(true)} />;

  const availableYears = [...new Set(records.map(r=>r.year).filter(Boolean))].sort((a,b)=>b-a);

  const filtered = records.filter(r => {
    if (filterCarrier !== "All" && r.carrier !== filterCarrier) return false;
    if (filterYear !== "All" && r.year !== parseInt(filterYear)) return false;
    if (filterQuarter !== "All") {
      const q = parseInt(filterQuarter);
      const monthRanges = {1:[1,2,3],2:[4,5,6],3:[7,8,9],4:[10,11,12]};
      if (!monthRanges[q].includes(r.month)) return false;
    }
    if (filterMonth !== "All" && r.month !== parseInt(filterMonth)) return false;
    if (filterProduct !== "All" && r.product_type !== filterProduct) return false;
    if (dateFrom) {
      const recDate = new Date(r.year, r.month-1, 1);
      if (recDate < new Date(dateFrom)) return false;
    }
    if (dateTo) {
      const recDate = new Date(r.year, r.month-1, 1);
      if (recDate > new Date(dateTo)) return false;
    }
    return true;
  });

  const totalAP = filtered.reduce((s,r)=>s+(r.annualized_premium||0),0);
  const totalComm = filtered.reduce((s,r)=>s+(r.commission_amount||0),0);
  const totalChargeback = filtered.reduce((s,r)=>s+(r.chargeback_amount||0),0);
  const netComm = totalComm - totalChargeback;
  const totalPolicies = filtered.filter(r=>r.chargeback_amount===0).length;

  const byCarrier = CARRIERS.map((c,i) => {
    const rows = filtered.filter(r=>r.carrier===c);
    return { name:c, AP:rows.reduce((s,r)=>s+(r.annualized_premium||0),0), Commission:rows.reduce((s,r)=>s+(r.commission_amount||0),0), Chargebacks:rows.reduce((s,r)=>s+(r.chargeback_amount||0),0), Policies:rows.filter(r=>r.chargeback_amount===0).length, color:CARRIER_COLORS[i] };
  }).filter(c=>c.AP>0).sort((a,b)=>b.AP-a.AP);

  const monthlyTrend = MONTHS.map((m,i) => {
    const rows = filtered.filter(r=>r.month===i+1);
    return { month:m, "Ann. Premium":rows.reduce((s,r)=>s+(r.annualized_premium||0),0), "Net Commission":rows.reduce((s,r)=>s+(r.commission_amount||0)-(r.chargeback_amount||0),0) };
  }).filter(m=>m["Ann. Premium"]>0);

  const fmt = (n) => n >= 1000000 ? `$${(n/1000000).toFixed(2)}M` : n >= 1000 ? `$${(n/1000).toFixed(1)}K` : `$${n.toFixed(0)}`;
  const fmtFull = (n) => `$${Number(n||0).toLocaleString("en-US",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  const clearFilters = () => { setFilterCarrier("All");setFilterYear("All");setFilterQuarter("All");setFilterMonth("All");setFilterProduct("All");setDateFrom("");setDateTo(""); };
  const hasFilters = filterCarrier!=="All"||filterYear!=="All"||filterQuarter!=="All"||filterMonth!=="All"||filterProduct!=="All"||dateFrom||dateTo;

  const exportCSV = () => {
    const headers = ["Carrier","Policy Number","Client","Product","Month","Year","Ann. Premium","Commission %","Commission $","Chargeback $","Status"];
    const rows = filtered.map(r=>[r.carrier,r.policy_number,r.client_name,r.product_type,r.month,r.year,r.annualized_premium,r.commission_pct,r.commission_amount,r.chargeback_amount,r.status]);
    const csv = [headers,...rows].map(r=>r.join(",")).join("\n");
    const blob = new Blob([csv],{type:"text/csv"});
    const a = document.createElement("a"); a.href=URL.createObjectURL(blob); a.download="syndicate_commissions.csv"; a.click();
  };

  const handleImport = (count) => { fetchRecords(); setActiveTab("dashboard"); alert(`${count} records saved to database successfully!`); };
  const tabs = [{id:"dashboard",label:"Dashboard",icon:"📊"},{id:"carriers",label:"By Carrier",icon:"🏢"},{id:"records",label:"All Records",icon:"📋"},{id:"upload",label:"Upload Data",icon:"📥"}];
  const products = ["All",...[...new Set(records.map(r=>r.product_type).filter(Boolean))]];

  const selectStyle = {padding:"8px 14px",borderRadius:10,border:"1.5px solid #e2e8f0",fontSize:13,color:"#374151",background:"#fff",fontWeight:500,cursor:"pointer"};

  return (
    <div style={{minHeight:"100vh",background:"#f0f4f8",fontFamily:"'DM Sans',system-ui,sans-serif"}}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;800&family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f0f4f8;}
        ::-webkit-scrollbar{width:6px;height:6px;}
        ::-webkit-scrollbar-track{background:#f1f5f9;}
        ::-webkit-scrollbar-thumb{background:#cbd5e1;border-radius:3px;}
        .tab-btn:hover{background:#eff6ff !important;}
        .row-hover:hover{background:#f8fafc !important;}
        .logout-btn:hover{background:rgba(255,255,255,0.15) !important;}
      `}</style>

      <header style={{background:"linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1d4ed8 100%)",padding:"0 32px",boxShadow:"0 4px 24px rgba(15,23,42,0.3)"}}>
        <div style={{maxWidth:1400,margin:"0 auto",display:"flex",alignItems:"center",justifyContent:"space-between",height:72}}>
          <div style={{display:"flex",alignItems:"center",gap:16}}>
            <div style={{width:42,height:42,borderRadius:12,background:"linear-gradient(135deg,#3b82f6,#60a5fa)",display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,boxShadow:"0 2px 12px rgba(59,130,246,0.4)"}}>⚡</div>
            <div>
              <h1 style={{fontFamily:"'Playfair Display',serif",fontSize:20,color:"#fff",fontWeight:800,lineHeight:1}}>Syndicate Financial</h1>
              <p style={{color:"#93c5fd",fontSize:11,letterSpacing:"0.12em",textTransform:"uppercase",marginTop:2}}>Commission Intelligence Dashboard</p>
            </div>
          </div>
          <div style={{display:"flex",alignItems:"center",gap:12}}>
            <span style={{color:"#93c5fd",fontSize:12}}>{records.length} total records</span>
            <div style={{width:1,height:20,background:"rgba(255,255,255,0.15)"}}/>
            <button className="logout-btn" onClick={()=>setLoggedIn(false)} style={{padding:"6px 14px",borderRadius:8,border:"1.5px solid rgba(255,255,255,0.2)",background:"transparent",color:"#93c5fd",fontSize:12,fontWeight:600,cursor:"pointer",transition:"all 0.15s"}}>Sign Out</button>
          </div>
        </div>
      </header>

      <div style={{background:"#fff",borderBottom:"1.5px solid #e2e8f0",padding:"0 32px",boxShadow:"0 1px 4px rgba(15,23,42,0.05)"}}>
        <div style={{maxWidth:1400,margin:"0 auto",display:"flex",gap:4}}>
          {tabs.map(t=>(
            <button key={t.id} className="tab-btn" onClick={()=>setActiveTab(t.id)} style={{padding:"16px 24px",border:"none",background:"transparent",cursor:"pointer",fontSize:14,fontWeight:activeTab===t.id?700:500,color:activeTab===t.id?"#1d4ed8":"#64748b",borderBottom:activeTab===t.id?"3px solid #1d4ed8":"3px solid transparent",transition:"all 0.15s",display:"flex",alignItems:"center",gap:8}}>
              {t.icon} {t.label}
            </button>
          ))}
          <div style={{flex:1}}/>
          <button onClick={fetchRecords} style={{margin:"10px 4px",padding:"0 16px",borderRadius:10,border:"1.5px solid #e2e8f0",background:"#fff",color:"#64748b",fontWeight:600,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}>🔄 Refresh</button>
          <button onClick={exportCSV} style={{margin:"10px 0",padding:"0 20px",borderRadius:10,border:"1.5px solid #1d4ed8",background:"#fff",color:"#1d4ed8",fontWeight:700,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}>⬇️ Export CSV</button>
        </div>
      </div>

      <main style={{maxWidth:1400,margin:"0 auto",padding:"28px 32px"}}>

        {loading && (
          <div style={{textAlign:"center",padding:"80px 0"}}>
            <div style={{fontSize:40,marginBottom:16}}>⏳</div>
            <p style={{color:"#64748b",fontSize:16,fontWeight:600}}>Loading from database...</p>
          </div>
        )}

        {!loading && activeTab !== "upload" && (
          <div style={{background:"#fff",borderRadius:14,border:"1.5px solid #e2e8f0",padding:"16px 20px",marginBottom:24,display:"flex",gap:10,flexWrap:"wrap",alignItems:"center"}}>
            <span style={{color:"#94a3b8",fontSize:12,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase"}}>FILTER</span>

            <select value={filterYear} onChange={e=>{setFilterYear(e.target.value);setFilterQuarter("All");setFilterMonth("All");setDateFrom("");setDateTo("");}} style={selectStyle}>
              <option value="All">All Years</option>
              {availableYears.map(y=><option key={y} value={y}>{y}</option>)}
            </select>

            <select value={filterQuarter} onChange={e=>{setFilterQuarter(e.target.value);setFilterMonth("All");}} style={selectStyle}>
              <option value="All">All Quarters</option>
              <option value="1">Q1 (Jan–Mar)</option>
              <option value="2">Q2 (Apr–Jun)</option>
              <option value="3">Q3 (Jul–Sep)</option>
              <option value="4">Q4 (Oct–Dec)</option>
            </select>

            <select value={filterMonth} onChange={e=>setFilterMonth(e.target.value)} style={selectStyle}>
              <option value="All">All Months</option>
              {MONTHS.map((m,i)=><option key={m} value={i+1}>{m}</option>)}
            </select>

            <select value={filterCarrier} onChange={e=>setFilterCarrier(e.target.value)} style={selectStyle}>
              <option value="All">All Carriers</option>
              {CARRIERS.map(c=><option key={c}>{c}</option>)}
            </select>

            <select value={filterProduct} onChange={e=>setFilterProduct(e.target.value)} style={selectStyle}>
              {products.map(p=><option key={p}>{p}</option>)}
            </select>

            <div style={{display:"flex",alignItems:"center",gap:6,marginLeft:4}}>
              <span style={{color:"#94a3b8",fontSize:11,fontWeight:600}}>FROM</span>
              <input type="month" value={dateFrom} onChange={e=>{setDateFrom(e.target.value);setFilterYear("All");setFilterQuarter("All");setFilterMonth("All");}} style={{...selectStyle,padding:"7px 10px"}} />
              <span style={{color:"#94a3b8",fontSize:11,fontWeight:600}}>TO</span>
              <input type="month" value={dateTo} onChange={e=>{setDateTo(e.target.value);setFilterYear("All");setFilterQuarter("All");setFilterMonth("All");}} style={{...selectStyle,padding:"7px 10px"}} />
            </div>

            {hasFilters && (
              <button onClick={clearFilters} style={{padding:"8px 14px",borderRadius:10,border:"1.5px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontWeight:600,cursor:"pointer",fontSize:13}}>✕ Clear</button>
            )}
            <span style={{marginLeft:"auto",color:"#64748b",fontSize:13,fontWeight:600}}>{filtered.length} records</span>
          </div>
        )}

        {!loading && activeTab === "dashboard" && (
          <div>
            {records.length === 0 ? (
              <div style={{textAlign:"center",padding:"80px 0",background:"#fff",borderRadius:16,border:"1.5px solid #e2e8f0"}}>
                <div style={{fontSize:48,marginBottom:16}}>📭</div>
                <h3 style={{fontFamily:"'Playfair Display',serif",color:"#0f172a",fontSize:22,marginBottom:8}}>No commission data yet</h3>
                <p style={{color:"#64748b",fontSize:15,marginBottom:24}}>Upload your first commission statement to get started</p>
                <button onClick={()=>setActiveTab("upload")} style={{padding:"12px 28px",borderRadius:12,border:"none",background:"linear-gradient(135deg,#1d4ed8,#2563eb)",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:15}}>📥 Upload First Statement</button>
              </div>
            ) : (
              <>
                <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:20,marginBottom:28}}>
                  <KPICard label="Total Ann. Premium" value={fmt(totalAP)} sub={`${totalPolicies} policies`} color="#1d4ed8" icon="📈" />
                  <KPICard label="Gross Commissions" value={fmt(totalComm)} sub="Before chargebacks" color="#0891b2" icon="💰" />
                  <KPICard label="Chargebacks / Reversals" value={fmtFull(totalChargeback)} sub={`${filtered.filter(r=>r.chargeback_amount>0).length} policies affected`} color="#dc2626" icon="⚠️" />
                  <KPICard label="Net Commissions" value={fmt(netComm)} sub="After chargebacks" color="#059669" icon="✅" />
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1.6fr 1fr",gap:20,marginBottom:28}}>
                  <div style={{background:"#fff",borderRadius:16,padding:24,boxShadow:"0 2px 16px rgba(30,64,175,0.06)",border:"1.5px solid #e2e8f0"}}>
                    <h3 style={{fontFamily:"'Playfair Display',serif",color:"#0f172a",marginBottom:4,fontSize:17}}>Monthly Trend</h3>
                    <p style={{color:"#94a3b8",fontSize:12,marginBottom:20}}>Annualized Premium vs Net Commission</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={monthlyTrend}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                        <XAxis dataKey="month" tick={{fontSize:12,fill:"#94a3b8"}} axisLine={false} tickLine={false} />
                        <YAxis tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}K`} />
                        <Tooltip formatter={(v)=>fmtFull(v)} contentStyle={{borderRadius:10,border:"1px solid #e2e8f0",fontSize:13}} />
                        <Legend />
                        <Line type="monotone" dataKey="Ann. Premium" stroke="#1d4ed8" strokeWidth={2.5} dot={{fill:"#1d4ed8",r:4}} />
                        <Line type="monotone" dataKey="Net Commission" stroke="#059669" strokeWidth={2.5} dot={{fill:"#059669",r:4}} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <div style={{background:"#fff",borderRadius:16,padding:24,boxShadow:"0 2px 16px rgba(30,64,175,0.06)",border:"1.5px solid #e2e8f0"}}>
                    <h3 style={{fontFamily:"'Playfair Display',serif",color:"#0f172a",marginBottom:4,fontSize:17}}>Top Carriers by AP</h3>
                    <p style={{color:"#94a3b8",fontSize:12,marginBottom:20}}>Annualized Premium ranking</p>
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={byCarrier.slice(0,6)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                        <XAxis type="number" tick={{fontSize:11,fill:"#94a3b8"}} axisLine={false} tickLine={false} tickFormatter={v=>`$${(v/1000).toFixed(0)}K`} />
                        <YAxis dataKey="name" type="category" tick={{fontSize:11,fill:"#374151",fontWeight:600}} axisLine={false} tickLine={false} width={90} />
                        <Tooltip formatter={(v)=>fmtFull(v)} contentStyle={{borderRadius:10,border:"1px solid #e2e8f0",fontSize:13}} />
                        <Bar dataKey="AP" radius={[0,6,6,0]} fill="#1d4ed8" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div style={{background:"#fff",borderRadius:16,border:"1.5px solid #e2e8f0",overflow:"hidden",boxShadow:"0 2px 16px rgba(30,64,175,0.06)"}}>
                  <div style={{padding:"20px 24px",borderBottom:"1.5px solid #f1f5f9"}}>
                    <h3 style={{fontFamily:"'Playfair Display',serif",color:"#0f172a",fontSize:17}}>Carrier Breakdown</h3>
                    <p style={{color:"#94a3b8",fontSize:12,marginTop:2}}>All active carriers with commission detail</p>
                  </div>
                  <div style={{overflowX:"auto"}}>
                    <table style={{width:"100%",borderCollapse:"collapse"}}>
                      <thead>
                        <tr style={{background:"#f8fafc"}}>
                          {["Carrier","Policies","Ann. Premium","Gross Commission","Chargebacks","Net Commission","Avg Comm %"].map(h=>(
                            <th key={h} style={{padding:"12px 18px",textAlign:"left",fontSize:12,fontWeight:700,color:"#64748b",letterSpacing:"0.04em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {byCarrier.map((c)=>(
                          <tr key={c.name} className="row-hover" style={{borderTop:"1px solid #f1f5f9",transition:"background 0.1s"}}>
                            <td style={{padding:"14px 18px"}}>
                              <div style={{display:"flex",alignItems:"center",gap:10}}>
                                <div style={{width:10,height:10,borderRadius:3,background:c.color,flexShrink:0}}/>
                                <span style={{fontWeight:700,color:"#0f172a",fontSize:14}}>{c.name}</span>
                              </div>
                            </td>
                            <td style={{padding:"14px 18px",color:"#374151",fontSize:14}}>{c.Policies}</td>
                            <td style={{padding:"14px 18px",color:"#1d4ed8",fontWeight:700,fontSize:14}}>{fmtFull(c.AP)}</td>
                            <td style={{padding:"14px 18px",color:"#0891b2",fontWeight:600,fontSize:14}}>{fmtFull(c.Commission)}</td>
                            <td style={{padding:"14px 18px",color:c.Chargebacks>0?"#dc2626":"#94a3b8",fontWeight:c.Chargebacks>0?700:400,fontSize:14}}>{c.Chargebacks>0?`-${fmtFull(c.Chargebacks)}`:"—"}</td>
                            <td style={{padding:"14px 18px",color:"#059669",fontWeight:700,fontSize:14}}>{fmtFull(c.Commission-c.Chargebacks)}</td>
                            <td style={{padding:"14px 18px",fontSize:14}}>
                              <span style={{padding:"4px 10px",borderRadius:20,background:"#f0f9ff",color:"#0369a1",fontWeight:700,fontSize:12}}>{c.AP>0?(((c.Commission-c.Chargebacks)/c.AP)*100).toFixed(2):0}%</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{background:"#f0f9ff",borderTop:"2px solid #bfdbfe"}}>
                          <td style={{padding:"14px 18px",fontWeight:800,color:"#0f172a",fontSize:14}}>TOTAL</td>
                          <td style={{padding:"14px 18px",fontWeight:700,color:"#0f172a",fontSize:14}}>{totalPolicies}</td>
                          <td style={{padding:"14px 18px",fontWeight:800,color:"#1d4ed8",fontSize:14}}>{fmtFull(totalAP)}</td>
                          <td style={{padding:"14px 18px",fontWeight:800,color:"#0891b2",fontSize:14}}>{fmtFull(totalComm)}</td>
                          <td style={{padding:"14px 18px",fontWeight:800,color:"#dc2626",fontSize:14}}>{totalChargeback>0?`-${fmtFull(totalChargeback)}`:"—"}</td>
                          <td style={{padding:"14px 18px",fontWeight:800,color:"#059669",fontSize:14}}>{fmtFull(netComm)}</td>
                          <td style={{padding:"14px 18px",fontWeight:700}}>
                            <span style={{padding:"4px 10px",borderRadius:20,background:"#dbeafe",color:"#1d4ed8",fontWeight:800,fontSize:12}}>{totalAP>0?(((totalComm-totalChargeback)/totalAP)*100).toFixed(2):0}%</span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && activeTab === "carriers" && (
          <div>
            {byCarrier.length === 0 ? (
              <div style={{textAlign:"center",padding:"80px 0",background:"#fff",borderRadius:16,border:"1.5px solid #e2e8f0"}}>
                <div style={{fontSize:48,marginBottom:16}}>📭</div>
                <p style={{color:"#64748b",fontSize:15}}>No carrier data yet. Upload commission statements to see carrier breakdown.</p>
              </div>
            ) : (
              <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:20}}>
                {byCarrier.map((c)=>(
                  <div key={c.name} style={{background:"#fff",borderRadius:16,padding:24,border:"1.5px solid #e2e8f0",boxShadow:"0 2px 12px rgba(30,64,175,0.06)",position:"relative",overflow:"hidden"}}>
                    <div style={{position:"absolute",top:0,left:0,right:0,height:4,background:c.color}}/>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:18}}>
                      <h3 style={{fontWeight:800,color:"#0f172a",fontSize:15}}>{c.name}</h3>
                      <span style={{padding:"3px 10px",borderRadius:20,background:"#f0f9ff",color:"#0369a1",fontWeight:700,fontSize:11}}>{c.Policies} policies</span>
                    </div>
                    <div style={{display:"grid",gap:10}}>
                      {[["Ann. Premium",fmtFull(c.AP),"#1d4ed8"],["Gross Commission",fmtFull(c.Commission),"#0891b2"],["Chargebacks",c.Chargebacks>0?`-${fmtFull(c.Chargebacks)}`:"None","#dc2626"],["Net Commission",fmtFull(c.Commission-c.Chargebacks),"#059669"]].map(([lbl,val,col])=>(
                        <div key={lbl} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"8px 12px",background:"#f8fafc",borderRadius:8}}>
                          <span style={{fontSize:12,color:"#64748b",fontWeight:600}}>{lbl}</span>
                          <span style={{fontSize:13,fontWeight:800,color:col}}>{val}</span>
                        </div>
                      ))}
                    </div>
                    <div style={{marginTop:14,padding:"10px 12px",background:"#eff6ff",borderRadius:8,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                      <span style={{fontSize:12,color:"#3b82f6",fontWeight:600}}>Eff. Commission Rate</span>
                      <span style={{fontSize:14,fontWeight:800,color:"#1d4ed8"}}>{c.AP>0?(((c.Commission-c.Chargebacks)/c.AP)*100).toFixed(2):0}%</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {!loading && activeTab === "records" && (
          <div style={{background:"#fff",borderRadius:16,border:"1.5px solid #e2e8f0",overflow:"hidden",boxShadow:"0 2px 16px rgba(30,64,175,0.06)"}}>
            <div style={{padding:"20px 24px",borderBottom:"1.5px solid #f1f5f9",display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:12}}>
              <div>
                <h3 style={{fontFamily:"'Playfair Display',serif",color:"#0f172a",fontSize:17}}>All Commission Records</h3>
                <p style={{color:"#94a3b8",fontSize:12,marginTop:2}}>{filtered.length} records · Sorted newest first</p>
              </div>
              <div style={{display:"flex",gap:8,flexWrap:"wrap",alignItems:"center"}}>
                {selectedIds.size > 0 && (
                  <button onClick={deleteSelected} disabled={deleting} style={{padding:"8px 16px",borderRadius:10,border:"none",background:"#dc2626",color:"#fff",fontWeight:700,cursor:"pointer",fontSize:13,display:"flex",alignItems:"center",gap:6}}>
                    🗑 Delete Selected ({selectedIds.size})
                  </button>
                )}
                {/* Delete by upload date dropdown */}
                {[...new Set(records.map(r=>r.upload_date).filter(Boolean))].length > 0 && (
                  <select onChange={e=>{if(e.target.value)deleteByUploadDate(e.target.value);e.target.value="";}} style={{padding:"8px 12px",borderRadius:10,border:"1.5px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontWeight:600,cursor:"pointer",fontSize:13}}>
                    <option value="">🗑 Delete by Upload Date</option>
                    {[...new Set(records.map(r=>r.upload_date).filter(Boolean))].sort().reverse().map(d=>(
                      <option key={d} value={d}>{d} ({records.filter(r=>r.upload_date===d).length} records)</option>
                    ))}
                  </select>
                )}
                {/* Delete by carrier dropdown */}
                {[...new Set(records.map(r=>r.carrier).filter(Boolean))].length > 0 && (
                  <select onChange={e=>{if(e.target.value)deleteByCarrier(e.target.value);e.target.value="";}} style={{padding:"8px 12px",borderRadius:10,border:"1.5px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontWeight:600,cursor:"pointer",fontSize:13}}>
                    <option value="">🗑 Delete by Carrier</option>
                    {[...new Set(records.map(r=>r.carrier).filter(Boolean))].sort().map(c=>(
                      <option key={c} value={c}>{c} ({records.filter(r=>r.carrier===c).length} records)</option>
                    ))}
                  </select>
                )}
              </div>
            </div>
            {filtered.length === 0 ? (
              <div style={{textAlign:"center",padding:"60px 0"}}>
                <div style={{fontSize:40,marginBottom:12}}>📭</div>
                <p style={{color:"#64748b",fontSize:15}}>No records found. Try adjusting your filters or upload commission statements.</p>
              </div>
            ) : (
              <div style={{overflowX:"auto"}}>
                <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                  <thead>
                    <tr style={{background:"#f8fafc"}}>
                      <th style={{padding:"11px 16px",width:40}}>
                        <input type="checkbox" checked={selectedIds.size===filtered.length&&filtered.length>0} onChange={toggleSelectAll} style={{cursor:"pointer",width:15,height:15}} />
                      </th>
                      {["Carrier","Client Name","Policy #","Product","Month","Year","Ann. Premium","Commission $","Chargeback","Status","Uploaded",""].map(h=>(
                        <th key={h} style={{padding:"11px 16px",textAlign:"left",fontSize:11,fontWeight:700,color:"#64748b",letterSpacing:"0.05em",textTransform:"uppercase",whiteSpace:"nowrap"}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((r)=>(
                      <tr key={r.id} className="row-hover" style={{borderTop:"1px solid #f1f5f9",transition:"background 0.1s",background:selectedIds.has(r.id)?"#fef2f2":""}}>
                        <td style={{padding:"11px 16px"}}>
                          <input type="checkbox" checked={selectedIds.has(r.id)} onChange={()=>toggleSelect(r.id)} style={{cursor:"pointer",width:15,height:15}} />
                        </td>
                        <td style={{padding:"11px 16px",fontWeight:700,color:"#1e3a8a"}}>{r.carrier}</td>
                        <td style={{padding:"11px 16px",color:"#374151"}}>{r.client_name||"—"}</td>
                        <td style={{padding:"11px 16px",color:"#64748b",fontFamily:"monospace",fontSize:12}}>{r.policy_number||"—"}</td>
                        <td style={{padding:"11px 16px"}}><span style={{padding:"3px 8px",borderRadius:6,background:"#f1f5f9",color:"#475569",fontWeight:600,fontSize:11}}>{r.product_type||"—"}</span></td>
                        <td style={{padding:"11px 16px",color:"#64748b"}}>{r.month ? MONTHS[r.month-1] : "—"}</td>
                        <td style={{padding:"11px 16px",color:"#64748b"}}>{r.year||"—"}</td>
                        <td style={{padding:"11px 16px",color:"#1d4ed8",fontWeight:700}}>{fmtFull(r.annualized_premium)}</td>
                        <td style={{padding:"11px 16px",color:"#059669",fontWeight:700}}>{fmtFull(r.commission_amount)}</td>
                        <td style={{padding:"11px 16px",color:r.chargeback_amount>0?"#dc2626":"#94a3b8",fontWeight:r.chargeback_amount>0?700:400}}>{r.chargeback_amount>0?`-${fmtFull(r.chargeback_amount)}`:"—"}</td>
                        <td style={{padding:"11px 16px"}}><span style={{padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:700,background:r.status==="Chargeback"?"#fef2f2":"#f0fdf4",color:r.status==="Chargeback"?"#dc2626":"#16a34a"}}>{r.status||"Paid"}</span></td>
                        <td style={{padding:"11px 16px",color:"#94a3b8",fontSize:12}}>{r.upload_date||"—"}</td>
                        <td style={{padding:"11px 16px"}}>
                          <button onClick={()=>deleteRecord(r.id)} style={{padding:"4px 10px",borderRadius:8,border:"1.5px solid #fca5a5",background:"#fef2f2",color:"#dc2626",fontWeight:700,cursor:"pointer",fontSize:12}}>🗑</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === "upload" && (
          <div style={{background:"#fff",borderRadius:16,border:"1.5px solid #e2e8f0",overflow:"hidden",boxShadow:"0 2px 16px rgba(30,64,175,0.06)"}}>
            <UploadPanel onImport={handleImport} />
          </div>
        )}

      </main>

      <footer style={{padding:"20px 32px",textAlign:"center",color:"#94a3b8",fontSize:12,borderTop:"1px solid #e2e8f0",background:"#fff",marginTop:16}}>
        Syndicate Financial · Commission Intelligence Dashboard · Powered by Supabase
      </footer>
    </div>
  );
}